import {Form, Input, Select, Spin, Switch} from 'antd';
import {RightOutlined} from '@ant-design/icons';
import styles from './userRoleSettings.module.scss';
import backIcon from 'images/back.png';
import {useEffect, useMemo, useState} from 'react';
import plusIcon from 'images/plus-circle.png';
import {useNavigate, useParams} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {CREATE_ADMIN_ROLE, UPDATE_ADMIN_ROLE} from 'graphql/mutations/admin';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {toast} from 'react-toastify';
import {GET_USER_ROLE_BY_ID} from 'graphql/queries/admin';
import {GET_ADMIN_DASHBOARD_MENU} from 'graphql/queries/adminDashboardMenu';
import trashIcon from 'images/trash-can-outline.svg';

const operations = [
  {key: 'VIEW', name: 'View'},
  {key: 'CREATE', name: 'Create'},
  {key: 'DELETE', name: 'Delete'},
  {key: 'EXPORT', name: 'Export'},
  {key: 'UPDATE', name: 'Update'},
  {key: 'ALL', name: 'All'},
];

// A role can be limited to specific products. An empty list is not "no access"
// — it means every product, which is what every pre-existing role carries.
// There is deliberately no way to express "no products".
const products = [
  {key: 'MOBILE', name: 'Mobile'},
  {key: 'ACADEMY', name: 'Academy'},
];

const sameProducts = (a = [], b = []) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

// Backend stores one {subTab, operations} entry per sub-tab. In the UI we group
// sub-tabs that share the exact same operations into a single multi-select row,
// so an admin can grant e.g. KYC + Overview the same operations at once.
const groupSubTabsByOperations = (subTabs = []) => {
  const groups = {};
  subTabs.forEach(sub => {
    const ops = sub?.operations || [];
    const signature = [...ops].sort().join(',');
    if (!groups[signature]) {
      groups[signature] = {subTab: [], operations: ops};
    }
    if (sub?.subTab) groups[signature].subTab.push(sub.subTab);
  });
  return Object.values(groups);
};

const UserRoleSettings = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const {id} = useParams();
  const [tabs, setTabs] = useState([]);
  // Per-card collapse state — expanded by default (absent === open).
  const [collapsed, setCollapsed] = useState({});
  // What the role was loaded with, so an edit that never touches Products can
  // omit the field entirely instead of rewriting it.
  const [initialProducts, setInitialProducts] = useState([]);

  const toggleCollapse = index =>
    setCollapsed(prev => ({...prev, [index]: !prev[index]}));

  const {data, loading} = useQuery(GET_USER_ROLE_BY_ID, {
    variables: {
      getUserRoleByIdId: id,
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !id,
  });

  const {data: menuData, loading: menuLoading} = useQuery(
    GET_ADMIN_DASHBOARD_MENU,
    {errorPolicy: 'all'},
  );

  // Catalog tabs (already alphabetized server-side) + a key -> sub-tabs lookup.
  const catalogTabs = useMemo(
    () => menuData?.getAdminDashboardMenu?.data || [],
    [menuData],
  );

  const subTabsByTabKey = useMemo(() => {
    const map = {};
    catalogTabs.forEach(tab => {
      map[tab.key] = tab.subTabs || [];
    });
    return map;
  }, [catalogTabs]);

  const getCatalogSubTabs = tabKey => subTabsByTabKey[tabKey] || [];

  useEffect(() => {
    if (data) {
      const loadedProducts = data?.getUserRoleById?.data?.products || [];
      form.setFieldsValue({
        name: data?.getUserRoleById?.data?.name,
        description: data?.getUserRoleById?.data?.description,
        products: loadedProducts,
      });
      setInitialProducts(loadedProducts);
      setTabs(
        (data?.getUserRoleById?.data?.tabs || []).map(tab => ({
          tab: tab?.tab,
          operations: tab?.operations || [],
          allSubTabs: !!tab?.allSubTabs,
          subTabs: groupSubTabsByOperations(tab?.subTabs),
        })),
      );
    }
  }, [data]);

  const [createUserRole, {loading: loadingCreate}] =
    useMutation(CREATE_ADMIN_ROLE);
  const [updateUserRole, {loading: loadingUpdate}] =
    useMutation(UPDATE_ADMIN_ROLE);

  const handleAddTabs = () => {
    setTabs([
      ...tabs,
      {
        tab: '',
        operations: [],
        allSubTabs: false,
        subTabs: [],
      },
    ]);
  };

  const handleChangeFieldValue = (value, name, index) => {
    const newFormValues = [...tabs];
    newFormValues[index] = {...newFormValues[index], [name]: value};
    // Switching the tab invalidates any previously picked sub-tabs.
    if (name === 'tab') {
      newFormValues[index].allSubTabs = false;
      newFormValues[index].subTabs = [];
    }
    setTabs(newFormValues);
  };

  const handleChangeSubTabValue = (value, name, tabIndex, subTabIndex) => {
    const newFormValues = [...tabs];
    const subTabs = [...(newFormValues[tabIndex].subTabs || [])];
    subTabs[subTabIndex] = {...subTabs[subTabIndex], [name]: value};
    newFormValues[tabIndex] = {...newFormValues[tabIndex], subTabs};
    setTabs(newFormValues);
  };

  const handleAddSubTab = tabIndex => {
    const newFormValues = [...tabs];
    newFormValues[tabIndex] = {
      ...newFormValues[tabIndex],
      subTabs: [
        ...(newFormValues[tabIndex].subTabs || []),
        {subTab: [], operations: []},
      ],
    };
    setTabs(newFormValues);
  };

  const handleDeleteSubTab = (tabIndex, subTabIndex) => {
    const newFormValues = [...tabs];
    const subTabs = [...(newFormValues[tabIndex].subTabs || [])];
    subTabs.splice(subTabIndex, 1);
    newFormValues[tabIndex] = {...newFormValues[tabIndex], subTabs};
    setTabs(newFormValues);
  };

  const handleDeleteTab = index => {
    const updatedTabs = [...tabs];
    updatedTabs.splice(index, 1);
    setTabs(updatedTabs);
  };

  // A tab grant is valid when it has a tab and non-empty operations. Sub-tabs
  // are optional: with the toggle off and no sub-tab rows it's a tab-level
  // grant (operations apply to the whole tab via the legacy fallback). Only
  // sub-tab rows the admin actually adds must each be complete.
  const isTabGrantInvalid = tab => {
    if (!tab.tab) return true;
    if (!tab.operations?.length) return true;

    const hasCatalogSubs = getCatalogSubTabs(tab.tab).length > 0;
    if (!hasCatalogSubs) return false; // page-level tab
    if (tab.allSubTabs === true) return false;

    // No sub-tab rows → tab-level grant, valid as-is.
    if (!tab.subTabs?.length) return false;
    // Any row that exists must have at least one sub-tab + non-empty operations.
    return tab.subTabs.some(
      sub => !sub.subTab?.length || !sub.operations?.length,
    );
  };

  // Build the new-shape payload. Never include `permissions`.
  const buildTabsPayload = () =>
    tabs.map(tab => {
      const hasCatalogSubs = getCatalogSubTabs(tab.tab).length > 0;
      const allSubTabs = hasCatalogSubs ? !!tab.allSubTabs : false;
      // Expand each multi-select row into one {subTab, operations} entry per
      // selected sub-tab; later rows win if a sub-tab is picked twice.
      let subTabs = [];
      if (hasCatalogSubs && !allSubTabs) {
        const byKey = {};
        (tab.subTabs || []).forEach(row => {
          (row.subTab || []).forEach(key => {
            byKey[key] = {subTab: key, operations: row.operations};
          });
        });
        subTabs = Object.values(byKey);
      }
      return {
        tab: tab.tab,
        operations: tab.operations,
        allSubTabs,
        subTabs,
      };
    });

  const handleFinish = async values => {
    if (tabs.some(isTabGrantInvalid)) {
      toast.error('Please fill out all fields for each tab.');
      return;
    }

    const transformedName = values.name.toUpperCase().replace(/\s/g, '_');
    const tabsPayload = buildTabsPayload();
    const selectedProducts = values.products || [];
    // Omitting `products` means "unchanged" server-side, so only send it when
    // the admin actually changed the selection — editing a role's name should
    // not rewrite its product list.
    const productsPayload = sameProducts(selectedProducts, initialProducts)
      ? {}
      : {products: selectedProducts};

    if (id) {
      updateUserRole({
        variables: {
          data: {
            id,
            name: transformedName,
            description: values.description,
            ...productsPayload,
            tabs: tabsPayload,
          },
        },
      })
        .then(({data: updateUserRole}) => {
          const {statusCode, __typename, message} =
            updateUserRole?.updateUserRole;
          if (__typename === 'Error' || statusCode === 400) {
            toast.error(`Something went wrong!!! - ${message} `);
          } else {
            toast.success('Updated Successfully');
            navigate('/dashboard/admin-settings?tab=role');
          }
        })
        .catch(err => toast.error(`Something went wrong!! - ${err.message}`));
    } else {
      createUserRole({
        variables: {
          data: {
            name: transformedName,
            description: values.name,
            // On create there is nothing to leave unchanged: send the picks, or
            // nothing at all for an all-products role.
            ...(selectedProducts.length ? {products: selectedProducts} : {}),
            tabs: tabsPayload,
          },
        },
      })
        .then(({data: createUserRole}) => {
          const {statusCode, __typename, message} =
            createUserRole?.createUserRole;
          if (__typename === 'Error' || statusCode === 400) {
            toast.error(`Something went wrong!!! - ${message} `);
          } else {
            toast.success('Created Successfully');
            navigate('/dashboard/admin-settings?tab=role');
          }
        })
        .catch(err => toast.error(`Something went wrong!! - ${err.message}`));
    }
  };

  if ((id && loading) || menuLoading) {
    return (
      <div className="query-loading">
        <h1>Loading Role</h1>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <img onClick={() => navigate(-1)} src={backIcon} alt="" />
        <h1>{id ? 'Update' : 'Create'} Role Settings</h1>
      </div>
      <div className={styles.content}>
        <Form form={form} onFinish={handleFinish} layout="vertical">
          <Form.Item
            rules={[
              {
                required: true,
                message: 'Required Field',
              },
            ]}
            className={styles.width}
            label="Name"
            name="name">
            <Input placeholder="Role Name" />
          </Form.Item>
          {id && (
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'Required Field',
                },
              ]}
              className={styles.width}
              label="Description"
              name="description">
              <Input placeholder="Role Description (Used as Label Field" />
            </Form.Item>
          )}
          <Form.Item
            className={styles.width}
            label="Products"
            name="products"
            initialValue={[]}
            extra="No products selected = access to all products">
            <Select allowClear mode="multiple" placeholder="All products">
              {products.map(product => (
                <Select.Option key={product.key}>{product.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <h2>Admin Access</h2>
          {tabs.map((tabGrant, index) => {
            const catalogSubTabs = getCatalogSubTabs(tabGrant.tab);
            const hasSubTabs = catalogSubTabs.length > 0;
            const isOpen = !collapsed[index];
            const cardTitle =
              catalogTabs.find(tab => tab.key === tabGrant.tab)?.label ||
              'New Access';
            return (
              <div className={styles.cardContainer} key={index}>
                <div className={styles.cardHeader}>
                  <div
                    className={styles.cardHeaderLeft}
                    onClick={() => toggleCollapse(index)}>
                    <RightOutlined
                      className={`${styles.chevron} ${
                        isOpen ? styles.chevronOpen : ''
                      }`}
                    />
                    <span>{cardTitle}</span>
                  </div>
                  <img
                    onClick={() => handleDeleteTab(index)}
                    src={trashIcon}
                    alt="Remove tab"
                  />
                </div>
                {isOpen && (
                  <div className={styles.cardBody}>
                    <Form.Item
                      required
                      className={styles.width}
                      label="Tab Name (Name of Navigation Tab in the Dashboard)">
                      <Select
                        showSearch
                        value={tabGrant.tab || undefined}
                        optionFilterProp="label"
                        style={{textTransform: 'capitalize'}}
                        placeholder="Select a tab"
                        options={catalogTabs
                          .filter(
                            catalogTab =>
                              catalogTab.key === tabGrant.tab ||
                              !tabs.some(
                                (other, otherIndex) =>
                                  otherIndex !== index &&
                                  other.tab === catalogTab.key,
                              ),
                          )
                          .map(tab => ({
                            value: tab.key,
                            label: tab.label,
                          }))}
                        onChange={e => handleChangeFieldValue(e, 'tab', index)}
                      />
                    </Form.Item>
                    <Form.Item
                      required
                      className={styles.width}
                      label="Operation (Admin Action Access)">
                      <Select
                        onChange={e =>
                          handleChangeFieldValue(e, 'operations', index)
                        }
                        value={tabGrant.operations}
                        mode="multiple">
                        {operations.map(op => (
                          <Select.Option key={op.key}>{op.name}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    {hasSubTabs && (
                      <>
                        <div className={styles.switchRow}>
                          <Switch
                            checked={!!tabGrant.allSubTabs}
                            onChange={checked =>
                              handleChangeFieldValue(
                                checked,
                                'allSubTabs',
                                index,
                              )
                            }
                          />
                          <span>Grant access to all sub-tabs</span>
                        </div>

                        {!tabGrant.allSubTabs && (
                          <div className={styles.subTabSection}>
                            <div className={styles.subTabSectionTitle}>
                              Sub-tab Access
                            </div>
                            {(tabGrant.subTabs || []).map(
                              (subTab, subTabIndex) => (
                                <div
                                  className={styles.subTabRow}
                                  key={subTabIndex}>
                                  <div className={styles.subTabField}>
                                    <Form.Item label="Sub-tab" required>
                                      <Select
                                        showSearch
                                        mode="multiple"
                                        optionFilterProp="label"
                                        placeholder="Select one or more sub-tabs"
                                        value={subTab.subTab || []}
                                        options={catalogSubTabs
                                          .filter(
                                            cat =>
                                              (subTab.subTab || []).includes(
                                                cat.key,
                                              ) ||
                                              !(tabGrant.subTabs || []).some(
                                                (otherRow, otherIndex) =>
                                                  otherIndex !== subTabIndex &&
                                                  (
                                                    otherRow.subTab || []
                                                  ).includes(cat.key),
                                              ),
                                          )
                                          .map(sub => ({
                                            value: sub.key,
                                            label: sub.label,
                                          }))}
                                        onChange={e =>
                                          handleChangeSubTabValue(
                                            e,
                                            'subTab',
                                            index,
                                            subTabIndex,
                                          )
                                        }
                                      />
                                    </Form.Item>
                                    <Form.Item label="Operations" required>
                                      <Select
                                        mode="multiple"
                                        value={subTab.operations}
                                        onChange={e =>
                                          handleChangeSubTabValue(
                                            e,
                                            'operations',
                                            index,
                                            subTabIndex,
                                          )
                                        }>
                                        {operations.map(op => (
                                          <Select.Option key={op.key}>
                                            {op.name}
                                          </Select.Option>
                                        ))}
                                      </Select>
                                    </Form.Item>
                                  </div>
                                  <img
                                    onClick={() =>
                                      handleDeleteSubTab(index, subTabIndex)
                                    }
                                    src={trashIcon}
                                    alt="Remove sub-tab"
                                  />
                                </div>
                              ),
                            )}
                            <div
                              className={styles.addSubTab}
                              onClick={() => handleAddSubTab(index)}>
                              <img src={plusIcon} alt="" />
                              <span>Add sub-tab</span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div onClick={handleAddTabs} className={styles['add-button']}>
            <img src={plusIcon} alt="" />
            <h1>Add Access</h1>
          </div>
          <SubmitButton
            label={id ? 'Update' : 'Create'}
            disabled={loadingCreate || loadingUpdate}
          />
        </Form>
      </div>
    </div>
  );
};

export default UserRoleSettings;
